<template>
<div class="ft-root" :style="themePageStyle">

  <nav class="ft-topbar">
    <div class="ft-tb-inner">
      <button class="ft-back" @click="goBack"><i class="mdi mdi-arrow-left"></i></button>
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" class="ft-logo" /></a>
      <span class="ft-tb-title">Post</span>
      <button class="ft-tb-feed" @click="$router.push('/forum')"><i class="mdi mdi-home"></i> Feed</button>
    </div>
  </nav>

  <div class="ft-wrap">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

    <div v-else-if="!topic" class="ft-empty">
      <i class="mdi mdi-forum-remove"></i>
      <h5>Post not found</h5>
      <button class="btn btn-warning" @click="$router.push('/forum')">Back to feed</button>
    </div>

    <div v-else>
      <!-- ===== MAIN POST CARD ===== -->
      <div class="ft-post">
        <div class="ft-post-head">
          <user-avatar :src="topic.author_avatar" :name="topic.author_name" size="md" />
          <div class="ft-post-head-body">
            <div class="ft-post-author">
              {{ topic.author_name && topic.author_name.trim() ? topic.author_name : 'A farmer' }}
              <span v-if="topic.shared_topic_id" class="ft-shared-lbl">shared a post</span>
            </div>
            <div class="ft-post-meta">
              <span>{{ relTime(topic.created_at) }}</span>
              <i class="mdi mdi-earth"></i>
              <span v-if="topic.location_label" class="ft-loc-inline"><i class="mdi mdi-map-marker"></i>{{ topic.location_label }}</span>
              <span v-if="topic.category && topic.category!=='general'" class="ft-cat-inline">· {{ topic.category }}</span>
            </div>
          </div>
          <button v-if="topic.author_id!==myUserId && !topic.i_follow" class="ft-follow-btn" @click="toggleFollowTopic"><i class="mdi mdi-plus"></i> Follow</button>
        </div>

        <div class="ft-badges" v-if="topic.is_pinned==1 || topic.status==='closed' || topic.is_answered==1 || topic.species_name">
          <span v-if="topic.species_name" class="ft-chip species">{{ topic.species_name }}</span>
          <span v-if="topic.is_pinned==1" class="ft-chip pin"><i class="mdi mdi-pin"></i> pinned</span>
          <span v-if="topic.is_answered==1" class="ft-chip answered"><i class="mdi mdi-check-decagram"></i> answered</span>
          <span v-if="topic.status==='closed'" class="ft-chip closed"><i class="mdi mdi-lock"></i> closed</span>
        </div>

        <h2 v-if="topic.title && topic.title!=='Post'" class="ft-title">{{ topic.title }}</h2>
        <div v-if="topic.content" class="ft-content">{{ topic.content }}</div>

        <!-- media grid -->
        <div v-if="topicVisualMedia.length" class="ft-media" :class="'n'+Math.min(topicVisualMedia.length,4)">
          <template v-for="(m, mi) in topicVisualMedia" :key="'tm-'+mi">
            <img v-if="m.type==='image'" :src="mediaUrl(m.url)" class="ft-media-cell" @click="lightbox=mediaUrl(m.url)" />
            <video v-else :src="mediaUrl(m.url)" class="ft-media-cell" controls preload="metadata"></video>
          </template>
        </div>
        <div v-if="topicDocs.length" class="ft-doc-list">
          <a v-for="(d, di) in topicDocs" :key="'td-'+di" :href="mediaUrl(d.url)" target="_blank" class="ft-doc-link">
            <i class="mdi" :class="docIcon(d.name || d.url)"></i>
            <div style="flex:1;min-width:0;"><div class="ft-doc-link-name">{{ d.name || 'Document' }}</div><div class="ft-doc-link-size" v-if="d.size">{{ humanSize(d.size) }}</div></div>
            <i class="mdi mdi-download" style="color:var(--fm-s,#6B6356);"></i>
          </a>
        </div>

        <!-- shared embed -->
        <div v-if="topic.shared_topic_id && topic.shared_content!=null" class="ft-shared" @click="$router.push('/forum/'+topic.shared_topic_id)">
          <div class="ft-shared-author"><i class="mdi mdi-share-variant me-1"></i>{{ topic.shared_author_name||'A farmer' }}</div>
          <div v-if="topic.shared_content" class="ft-shared-body">{{ topic.shared_content }}</div>
        </div>

        <!-- counts -->
        <div class="ft-counts">
          <span v-if="Number(topic.reaction_count)>0" class="ft-count-likes"><i class="mdi mdi-thumb-up"></i> {{ topic.reaction_count }}</span>
          <span class="ft-count-right">
            <span><i class="mdi mdi-eye-outline"></i> {{ topic.view_count }}</span>
            <span v-if="posts.length>0">{{ posts.length }} comment{{ posts.length>1?'s':'' }}</span>
            <span v-if="Number(topic.share_count)>0">{{ topic.share_count }} share{{ Number(topic.share_count)>1?'s':'' }}</span>
          </span>
        </div>

        <!-- action bar -->
        <div class="ft-actions">
          <button class="ft-act" :class="{liked:topic.my_reaction}" @click="reactTopic"><i class="mdi" :class="topic.my_reaction?'mdi-thumb-up':'mdi-thumb-up-outline'"></i> Like</button>
          <button class="ft-act" @click="focusReply"><i class="mdi mdi-comment-outline"></i> Comment</button>
          <button class="ft-act" :class="{shared:topic.i_shared}" @click="topic.i_shared ? unshareTopic() : openShare()"><i class="mdi" :class="topic.i_shared?'mdi-share':'mdi-share-outline'"></i> {{ topic.i_shared?'Shared':'Share' }}</button>
        </div>
      </div>

      <!-- ===== COMMENTS ===== -->
      <div class="ft-comments-card">
        <div class="ft-comments-head">{{ posts.length }} {{ posts.length === 1 ? 'comment' : 'comments' }}</div>

        <div v-for="p in orderedPosts" :key="p.id" class="ft-comment" :class="{accepted: p.is_accepted_answer == 1, 'ft-reply-indent': p.parent_post_id}">
          <user-avatar :src="p.author_avatar" :name="p.author_name" size="sm" :badge="p.author_is_vet == 1 ? 'vet' : ''" />
          <div class="ft-comment-col">
            <div class="ft-comment-bubble">
              <div class="ft-comment-author">
                {{ p.author_name && p.author_name.trim() ? p.author_name : 'A farmer' }}
                <span v-if="p.author_is_vet == 1" class="ft-mini-badge vet"><i class="mdi mdi-stethoscope"></i> Vet</span>
                <span v-if="p.is_vet_verified == 1" class="ft-mini-badge verified"><i class="mdi mdi-check-circle"></i> Vet-verified</span>
                <span v-if="p.is_accepted_answer == 1" class="ft-mini-badge accepted"><i class="mdi mdi-check-decagram"></i> Best answer</span>
              </div>
              <div v-if="p.content" class="ft-comment-text">{{ p.content }}</div>
            </div>

            <!-- reply media -->
            <div v-if="postVisualMedia(p).length" class="ft-comment-media">
              <template v-for="(m, mi) in postVisualMedia(p)" :key="'pm-'+p.id+'-'+mi">
                <img v-if="m.type==='image'" :src="mediaUrl(m.url)" class="ft-comment-img" @click="lightbox=mediaUrl(m.url)" />
                <video v-else :src="mediaUrl(m.url)" class="ft-comment-img" controls preload="metadata"></video>
              </template>
            </div>
            <div v-if="postDocs(p).length" class="ft-doc-list">
              <a v-for="(d, di) in postDocs(p)" :key="'pd-'+p.id+'-'+di" :href="mediaUrl(d.url)" target="_blank" class="ft-doc-link sm">
                <i class="mdi" :class="docIcon(d.name || d.url)"></i><span class="ft-doc-link-name">{{ d.name || 'Document' }}</span>
              </a>
            </div>

            <div class="ft-comment-foot">
              <span class="ft-cf-time">{{ relTime(p.created_at) }}</span>
              <button class="ft-cf-like" :class="{liked:p.my_reaction}" @click="reactReply(p)">Like</button>
              <span v-if="Number(p.reaction_count)>0" class="ft-cf-count"><i class="mdi mdi-thumb-up"></i> {{ p.reaction_count }}</span>
              <span v-if="p.location_label" class="ft-cf-loc"><i class="mdi mdi-map-marker"></i>{{ p.location_label }}</span>
            </div>

            <div class="ft-comment-actions" v-if="!isMyTopic && (canVerify || canAccept(p))">
              <button v-if="canAccept(p)" class="ft-sm-btn green" @click="acceptAnswer(p)" :disabled="acceptBusy"><i class="mdi mdi-check-decagram"></i> Best answer</button>
              <button v-if="canVerify && p.is_vet_verified != 1" class="ft-sm-btn blue" @click="verifyPost(p)"><i class="mdi mdi-stethoscope"></i> Vet-verify</button>
            </div>
          </div>
        </div>

        <!-- ===== REPLY COMPOSER ===== -->
        <div v-if="topic.status !== 'closed'" class="ft-reply">
          <user-avatar :src="myAvatar" :name="myName" size="sm" />
          <div class="ft-reply-col">
            <div class="ft-reply-field">
              <textarea ref="replyBox" v-model="replyContent" rows="1" placeholder="Write a comment…" @input="autoGrow"></textarea>
              <label class="ft-reply-attach" title="Attach photo, video or file">
                <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" multiple @change="onReplyMediaPick" />
                <i class="mdi mdi-camera"></i>
              </label>
            </div>

            <div v-if="replyMedia.length || replyCompressing" class="ft-reply-previews">
              <template v-for="(m, mi) in replyMedia" :key="mi">
                <div v-if="m.type !== 'document'" class="ft-rpv">
                  <img v-if="m.type === 'image'" :src="m.preview" />
                  <video v-else :src="m.preview" muted></video>
                  <button class="ft-rpv-x" @click="removeReplyMedia(mi)" type="button"><i class="mdi mdi-close"></i></button>
                </div>
                <div v-else class="ft-rpv ft-rpv-doc">
                  <i class="mdi" :class="docIcon(m.name)"></i>
                  <button class="ft-rpv-x" @click="removeReplyMedia(mi)" type="button"><i class="mdi mdi-close"></i></button>
                </div>
              </template>
              <span v-if="replyCompressing" class="spinner-border spinner-border-sm text-warning" style="margin:auto 4px;"></span>
            </div>

            <div class="ft-reply-bottom">
              <label class="ft-reply-loc">
                <input type="checkbox" v-model="replyShareLocation" @change="onReplyLocationToggle" />
                <i class="mdi mdi-map-marker"></i>
                <span v-if="replyLocationLabel">{{ replyLocationLabel }}</span>
                <span v-else-if="replyLocationBusy">Detecting…</span>
                <span v-else>Location</span>
              </label>
              <button class="ft-reply-send" @click="submitReply" :disabled="replyBusy || replyCompressing || !(replyContent.trim() || replyMedia.length)">
                <span v-if="replyBusy" class="spinner-border spinner-border-sm"></span>
                <i v-else class="mdi mdi-send"></i>
              </button>
            </div>
            <div v-if="replyError" class="ft-reply-err">{{ replyError }}</div>
          </div>
        </div>
        <div v-else class="ft-closed-banner"><i class="mdi mdi-lock me-1"></i>This post is closed for new comments.</div>
      </div>
    </div>
  </div>

  <!-- share modal -->
  <div v-if="shareModal.open" class="ft-mod-overlay" @click.self="closeShare">
    <div class="ft-mod-card">
      <h5 class="ft-mod-title"><i class="mdi mdi-share-variant me-2"></i>Share post</h5>

      <!-- ===== EXTERNAL SHARE (drives growth — friends see flockr.africa, tap, land on portal) ===== -->
      <div class="ft-ext-share">
        <div class="ft-ext-label">Share to</div>
        <div class="ft-ext-row">
          <button v-if="canWebShare" class="ft-ext-btn native" @click="shareNative" title="Share…"><i class="mdi mdi-share-variant"></i><span>Share…</span></button>
          <button class="ft-ext-btn fb" @click="shareTo('facebook')" title="Facebook"><i class="mdi mdi-facebook"></i><span>Facebook</span></button>
          <button class="ft-ext-btn wa" @click="shareTo('whatsapp')" title="WhatsApp"><i class="mdi mdi-whatsapp"></i><span>WhatsApp</span></button>
          <button class="ft-ext-btn tw" @click="shareTo('twitter')" title="X / Twitter"><i class="mdi mdi-twitter"></i><span>X</span></button>
          <button class="ft-ext-btn tg" @click="shareTo('telegram')" title="Telegram"><i class="mdi mdi-telegram"></i><span>Telegram</span></button>
          <button class="ft-ext-btn cp" @click="copyShareLink" title="Copy link"><i class="mdi" :class="copiedFlash?'mdi-check':'mdi-link-variant'"></i><span>{{ copiedFlash?'Copied':'Copy link' }}</span></button>
        </div>
      </div>

      <div class="ft-ext-divider"><span>or share to your Flockr followers</span></div>

      <div class="ft-share-author">
        <user-avatar :src="myAvatar" :name="myName" size="md" />
        <div><div class="ft-share-name">{{ myName }}</div><div class="ft-share-aud"><i class="mdi mdi-account-group"></i> Followers</div></div>
      </div>
      <textarea v-model="shareModal.note" class="form-control ft-input" rows="3" placeholder="Say something about this…"></textarea>
      <div class="ft-share-embed">
        <div class="ft-shared-author"><i class="mdi mdi-share-variant me-1"></i>{{ topic ? topic.author_name : '' }}</div>
        <div v-if="topic && topic.content" class="ft-shared-body">{{ topic.content }}</div>
      </div>
      <div class="d-flex gap-2 mt-3">
        <button class="btn btn-outline-secondary flex-fill" @click="closeShare" :disabled="shareModal.busy">Cancel</button>
        <button class="btn btn-warning flex-fill" @click="confirmShare" :disabled="shareModal.busy"><span v-if="shareModal.busy" class="spinner-border spinner-border-sm me-1"></span><i v-else class="mdi mdi-share me-1"></i>Share to followers</button>
      </div>
    </div>
  </div>

  <!-- lightbox -->
  <div v-if="lightbox" class="ft-lightbox" @click="lightbox=null"><img :src="lightbox" /></div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F0EBE4;--fm-bg3:#E9E4DC;--fm-br:#E2DCD3}
html[data-theme="light"] body{background:#EBE7E1!important}
html[data-theme="light"] .ft-root{background:#EBE7E1!important;color:#1A1A1A!important}
html[data-theme="light"] .ft-topbar{background:#FFFFFF!important;border-bottom-color:#E2DCD3!important}
html[data-theme="light"] .ft-post,
html[data-theme="light"] .ft-comments-card,
html[data-theme="light"] .ft-mod-card{background:#FFFFFF!important;border-color:#E2DCD3!important}
html[data-theme="light"] .ft-comment-bubble,
html[data-theme="light"] .ft-shared,
html[data-theme="light"] .ft-share-embed,
html[data-theme="light"] .ft-reply-field,
html[data-theme="light"] .ft-input{background:#EBE7E1!important;color:#1A1A1A!important}
html[data-theme="light"] .ft-act:hover{background:#EFEAE3!important}
</style>

<style scoped>
.ft-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.ft-topbar{position:sticky;top:0;z-index:100;background:var(--fm-bg1,#141414);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.ft-tb-inner{display:flex;align-items:center;gap:12px;max-width:680px;margin:0 auto;padding:10px 14px}
.ft-back{width:38px;height:38px;border-radius:50%;border:none;background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8);font-size:20px}
.ft-logo{height:30px}
.ft-tb-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:15px;flex:1}
.ft-tb-feed{background:rgba(191,161,100,.12);border:none;color:#BFA164;font-weight:700;font-size:13px;padding:7px 12px;border-radius:8px}
.ft-wrap{max-width:680px;margin:0 auto;padding:16px 14px 60px}
.ft-empty{text-align:center;padding:60px 20px}
.ft-empty i{font-size:64px;color:var(--fm-s,#3A3530)}
.ft-empty h5{color:var(--fm-h,#F5F0E8);margin:12px 0}

/* post card */
.ft-post{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:16px;margin-bottom:14px}
.ft-post-head{display:flex;align-items:flex-start;gap:10px}
.ft-post-head-body{flex:1;min-width:0}
.ft-post-author{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:15px}
.ft-shared-lbl{color:var(--fm-m,#A09888);font-weight:400;font-size:13px}
.ft-post-meta{display:flex;align-items:center;gap:5px;color:var(--fm-s,#6B6356);font-size:12px;margin-top:2px;flex-wrap:wrap}
.ft-post-meta i{font-size:13px}
.ft-loc-inline{display:inline-flex;align-items:center;gap:2px;color:#66BB6A}
.ft-cat-inline{text-transform:capitalize}
.ft-follow-btn{background:transparent;border:none;color:#BFA164;font-size:13px;font-weight:700;padding:4px 8px;border-radius:6px;white-space:nowrap;display:flex;align-items:center;gap:2px}
.ft-follow-btn:hover{background:rgba(191,161,100,.1)}
.ft-badges{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px}
.ft-chip{font-size:10px;font-weight:700;padding:3px 9px;border-radius:8px;text-transform:capitalize;display:inline-flex;align-items:center;gap:3px}
.ft-chip.species{background:rgba(191,161,100,.12);color:#BFA164}
.ft-chip.pin{background:rgba(191,161,100,.1);color:#BFA164}
.ft-chip.answered{background:rgba(80,200,120,.12);color:#50C878}
.ft-chip.closed{background:rgba(255,107,107,.1);color:#FF6B6B}
.ft-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:19px;line-height:1.3;margin:12px 0 6px}
.ft-content{color:var(--fm-h,#F5F0E8);font-size:15px;line-height:1.6;white-space:pre-wrap;margin-top:10px}

.ft-media{display:grid;gap:3px;margin:12px -16px 0}
.ft-media.n1{grid-template-columns:1fr}
.ft-media.n2,.ft-media.n3,.ft-media.n4{grid-template-columns:1fr 1fr}
.ft-media.n3>*:first-child{grid-column:1/-1}
.ft-media-cell{width:100%;height:100%;max-height:480px;object-fit:cover;background:var(--fm-bg2,#0F0F0F);cursor:pointer;display:block}
.ft-media.n1 .ft-media-cell{max-height:560px;object-fit:contain}

.ft-shared{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:10px 12px;margin-top:12px;cursor:pointer}
.ft-shared-author{color:#BFA164;font-size:12px;font-weight:700;margin-bottom:3px}
.ft-shared-body{color:var(--fm-m,#A09888);font-size:13px}

.ft-counts{display:flex;align-items:center;justify-content:space-between;padding:10px 2px;font-size:13px;color:var(--fm-s,#6B6356);border-bottom:1px solid var(--fm-br,#1E1E1E);margin-top:12px}
.ft-count-likes{display:flex;align-items:center;gap:5px}
.ft-count-likes i{color:#fff;border-radius:50%;font-size:10px;width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;background:linear-gradient(#18AFFF,#0062DF)}
.ft-count-right{display:flex;gap:12px}
.ft-count-right i{font-size:13px}
.ft-actions{display:flex;gap:4px;padding-top:6px}
.ft-act{flex:1;background:transparent;border:none;color:var(--fm-m,#A09888);font-size:14px;font-weight:700;padding:9px;border-radius:8px;display:flex;align-items:center;justify-content:center;gap:8px}
.ft-act:hover{background:var(--fm-bg2,#0F0F0F)}
.ft-act.liked{color:#1877F2}
.ft-act.shared{color:#45BD62}
.ft-act i{font-size:20px}

/* comments */
.ft-comments-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:16px}
.ft-comments-head{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:15px;margin-bottom:14px}
.ft-comment{display:flex;gap:8px;margin-bottom:14px}
.ft-comment.accepted .ft-comment-bubble{box-shadow:0 0 0 1.5px rgba(80,200,120,.5)}
.ft-reply-indent{margin-left:34px}
.ft-reply-indent .ft-comment-bubble{background:var(--fm-bg3,#1E1E1E)}
.ft-comment-col{flex:1;min-width:0}
.ft-comment-bubble{background:var(--fm-bg2,#0F0F0F);border-radius:16px;padding:8px 12px;display:inline-block;max-width:100%}
.ft-comment-author{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:13px}
.ft-mini-badge{display:inline-flex;align-items:center;gap:2px;font-size:9px;font-weight:700;padding:1px 6px;border-radius:6px;margin-left:5px}
.ft-mini-badge.vet,.ft-mini-badge.verified{background:rgba(79,195,247,.14);color:#4FC3F7}
.ft-mini-badge.accepted{background:rgba(80,200,120,.14);color:#50C878}
.ft-comment-text{color:var(--fm-h,#F5F0E8);font-size:14px;white-space:pre-wrap;margin-top:1px}
.ft-comment-media{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}
.ft-comment-img{max-width:200px;max-height:200px;border-radius:12px;object-fit:cover;cursor:pointer}
.ft-comment-foot{display:flex;align-items:center;gap:14px;margin:4px 0 0 12px;font-size:12px;color:var(--fm-s,#6B6356)}
.ft-cf-like{background:none;border:none;color:var(--fm-s,#6B6356);font-weight:700;font-size:12px;padding:0}
.ft-cf-like.liked{color:#1877F2}
.ft-cf-count{display:inline-flex;align-items:center;gap:3px;color:#4FC3F7}
.ft-cf-loc{display:inline-flex;align-items:center;gap:2px;color:#66BB6A}
.ft-comment-actions{display:flex;gap:6px;margin:6px 0 0 12px}
.ft-sm-btn{background:transparent;border:1px solid var(--fm-br,#1E1E1E);font-size:11px;font-weight:700;padding:4px 10px;border-radius:8px;display:inline-flex;align-items:center;gap:4px}
.ft-sm-btn.green{color:#50C878;border-color:rgba(80,200,120,.4)}
.ft-sm-btn.blue{color:#4FC3F7;border-color:rgba(79,195,247,.4)}

/* reply composer */
.ft-reply{display:flex;gap:8px;margin-top:8px;padding-top:12px;border-top:1px solid var(--fm-br,#1E1E1E)}
.ft-reply-col{flex:1;min-width:0}
.ft-reply-field{display:flex;align-items:flex-end;background:var(--fm-bg2,#0F0F0F);border-radius:18px;padding:4px 10px 4px 14px}
.ft-reply-field textarea{flex:1;background:transparent;border:none;outline:none;color:var(--fm-h,#F5F0E8);font-size:14px;resize:none;padding:8px 0;max-height:120px;line-height:1.4}
.ft-reply-attach{margin:0 0 6px;cursor:pointer;color:var(--fm-s,#6B6356);font-size:20px;display:flex}
.ft-reply-attach input{display:none}
.ft-reply-attach:hover{color:#BFA164}
.ft-reply-previews{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
.ft-rpv{position:relative;width:56px;height:56px;border-radius:8px;overflow:hidden;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E)}
.ft-rpv img,.ft-rpv video{width:100%;height:100%;object-fit:cover}
.ft-rpv-doc{display:flex;align-items:center;justify-content:center;color:#BFA164;font-size:22px}
.ft-rpv-x{position:absolute;top:1px;right:1px;width:18px;height:18px;border-radius:50%;background:rgba(0,0,0,.7);border:none;color:#FF6B6B;font-size:11px;display:flex;align-items:center;justify-content:center}
.ft-reply-bottom{display:flex;align-items:center;justify-content:space-between;margin-top:8px}
.ft-reply-loc{display:inline-flex;align-items:center;gap:5px;cursor:pointer;font-size:12px;color:var(--fm-m,#A09888);margin:0}
.ft-reply-loc input{accent-color:#50C878;width:14px;height:14px}
.ft-reply-loc i{color:#50C878}
.ft-reply-send{background:#BFA164;border:none;color:#0A0A0A;border-radius:50%;width:36px;height:36px;font-size:18px;flex-shrink:0}
.ft-reply-send:disabled{opacity:.4}
.ft-reply-err{color:#FF6B6B;font-size:12px;margin-top:6px}
.ft-closed-banner{margin-top:8px;padding:14px;background:rgba(255,107,107,.06);border:1px solid rgba(255,107,107,.2);border-radius:10px;color:#FF6B6B;font-size:13px;text-align:center}

/* doc links */
.ft-doc-list{display:flex;flex-direction:column;gap:6px;margin:10px 0}
.ft-doc-link{display:flex;align-items:center;gap:10px;padding:10px 12px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px;color:var(--fm-h,#F5F0E8);text-decoration:none;font-size:13px}
.ft-doc-link.sm{padding:8px 10px;font-size:12px}
.ft-doc-link:hover{border-color:#BFA164}
.ft-doc-link i{font-size:22px;color:#BFA164;flex-shrink:0}
.ft-doc-link-name{font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ft-doc-link-size{font-size:10px;color:var(--fm-s,#6B6356)}

/* modal + lightbox */
.ft-mod-overlay{position:fixed;inset:0;background:rgba(0,0,0,.7);backdrop-filter:blur(4px);z-index:2400;display:flex;align-items:center;justify-content:center;padding:20px}
.ft-mod-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:20px;max-width:480px;width:100%}
.ft-mod-title{color:var(--fm-h,#F5F0E8);font-size:18px;font-weight:700;margin-bottom:12px}
.ft-share-author{display:flex;align-items:center;gap:10px;margin-bottom:12px}
.ft-share-name{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px}
.ft-share-aud{display:inline-flex;align-items:center;gap:4px;background:var(--fm-bg2,#0F0F0F);color:var(--fm-m,#A09888);font-size:11px;padding:2px 8px;border-radius:6px;margin-top:2px}
.ft-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#1E1E1E)!important;color:var(--fm-h,#F5F0E8)!important;font-size:14px}
.ft-input:focus{border-color:#BFA164!important;box-shadow:0 0 0 2px rgba(191,161,100,.15)!important}
.ft-share-embed{margin-top:12px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:10px 12px}
.ft-shared-body{color:var(--fm-m,#A09888);font-size:13px}
.ft-lightbox{position:fixed;inset:0;background:rgba(0,0,0,.92);display:flex;align-items:center;justify-content:center;z-index:2500;padding:20px}
.ft-lightbox img{max-width:100%;max-height:100%;border-radius:8px}

/* external-share row */
.ft-ext-share{margin:-2px 0 14px}
.ft-ext-label{color:var(--fm-m,#A09888);font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px}
.ft-ext-row{display:flex;flex-wrap:wrap;gap:6px}
.ft-ext-btn{flex:1 1 84px;min-width:72px;display:flex;flex-direction:column;align-items:center;gap:4px;padding:10px 6px;border-radius:10px;border:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8);font-size:11px;font-weight:600;cursor:pointer;transition:transform .08s ease}
.ft-ext-btn:hover{transform:translateY(-1px);border-color:#BFA164}
.ft-ext-btn i{font-size:22px}
.ft-ext-btn.fb i{color:#1877F2}
.ft-ext-btn.wa i{color:#25D366}
.ft-ext-btn.tw i{color:#1DA1F2}
.ft-ext-btn.tg i{color:#2AABEE}
.ft-ext-btn.native i{color:#BFA164}
.ft-ext-btn.cp i{color:#BFA164}
.ft-ext-divider{position:relative;text-align:center;margin:6px 0 14px;font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:.5px}
.ft-ext-divider:before,.ft-ext-divider:after{content:'';position:absolute;top:50%;width:38%;height:1px;background:var(--fm-br,#1E1E1E)}
.ft-ext-divider:before{left:0}.ft-ext-divider:after{right:0}
.ft-ext-divider span{background:var(--fm-bg1,#141414);padding:0 10px;position:relative;z-index:1}
html[data-theme="light"] .ft-ext-divider span{background:#FFFFFF}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .ft-container { padding-top: 70px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .ft-card, .ft-post, .ft-reply { padding: 12px !important; border-radius: 10px !important; }
  .ft-input { font-size: 16px; }
  .ft-share-embed { padding: 8px 10px; }
  .ft-lightbox { padding: 10px; }
}
</style>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, portal as portalUrl, uploads } from "@/api";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";

function compressImage(file, maxDim, quality){
  return new Promise(function(resolve){
    var reader = new FileReader();
    reader.onload = function(e){
      var img = new Image();
      img.onload = function(){
        var w = img.width, h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) { h = Math.round(h * maxDim / w); w = maxDim; }
          else       { w = Math.round(w * maxDim / h); h = maxDim; }
        }
        var canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob(function(blob){
          if (!blob) { resolve({ file: file, preview: e.target.result }); return; }
          var emit = function(b){
            var name = file.name.replace(/\.[^.]+$/, '.jpg');
            var f = new File([b], name, { type: 'image/jpeg' });
            resolve({ file: f, preview: URL.createObjectURL(f) });
          };
          if (blob.size > 2 * 1024 * 1024) {
            canvas.toBlob(function(b2){ emit(b2 || blob); }, 'image/jpeg', 0.6);
          } else { emit(blob); }
        }, 'image/jpeg', quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default {
  name: "ForumTopicDetail",
  components: { UserAvatar },
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:true,
    topic: null, posts: [], lightbox: null,
    replyContent: '', replyBusy: false, replyError: '',
    replyMedia: [], replyCompressing: false,
    replyShareLocation: false, replyLocationLabel: '', replyLocationLat: null, replyLocationLng: null, replyLocationBusy: false, replyLocationError: '',
    acceptBusy: false,
    shareModal: { open:false, note:'', busy:false },
    copiedFlash: false
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#EBE7E1;min-height:100vh;color:#1A1A1A;'},
    myUserId(){return sessionStorage.getItem('userId')||''},
    canWebShare(){ return typeof navigator !== 'undefined' && typeof navigator.share === 'function'; },
    // OG-aware share URL — points at the API's share.php which serves Open Graph
    // tags to crawlers (so the FB/WhatsApp/X preview renders nicely) and JS-
    // redirects humans to the SPA forum page at flockr.africa.
    shareUrl(){
      if (!this.topic) return '';
      // Branded shortlink — flockr.africa/s/forum/<id>. The portal .htaccess
      // 302-proxies to the API's share.php which renders the OG card for the
      // social-media scraper, then JS-bounces real humans to /#/forum/<id>.
      var root = String(portalUrl || '').replace(/\/+$/, '');
      return root + '/s/forum/' + encodeURIComponent(this.topic.id);
    },
    shareText(){
      if (!this.topic) return '';
      var t = (this.topic.title && this.topic.title !== 'Post') ? this.topic.title : (this.topic.content || '').slice(0, 120);
      return (t ? (t + ' — ') : '') + 'on Flockr';
    },
    myName(){return sessionStorage.getItem('userName')||'You'},
    myAvatar(){return sessionStorage.getItem('userAvatar')||''},
    isMyTopic(){return this.topic && this.topic.author_id === this.myUserId;},
    orderedPosts(){
      var posts=this.posts||[];
      var tops=posts.filter(function(p){return !p.parent_post_id;});
      var out=[];
      tops.forEach(function(t){
        out.push(t);
        posts.forEach(function(r){ if(String(r.parent_post_id)===String(t.id)) out.push(r); });
      });
      posts.forEach(function(p){ if(p.parent_post_id && out.indexOf(p)<0) out.push(p); });
      return out;
    },
    canVerify(){
      try{var roles=JSON.parse(sessionStorage.getItem('userRoles')||'[]'); return roles.indexOf('vet')>-1;}catch(e){return false}
    },
    topicMedia(){
      if (!this.topic || !this.topic.photos) return [];
      try {
        var arr = typeof this.topic.photos === 'string' ? JSON.parse(this.topic.photos) : this.topic.photos;
        return Array.isArray(arr) ? arr.filter(function(m){return m && m.url;}) : [];
      } catch(e){ return []; }
    },
    topicVisualMedia(){ return this.topicMedia.filter(function(m){ return m.type !== 'document'; }); },
    topicDocs(){ return this.topicMedia.filter(function(m){ return m.type === 'document'; }); }
  },
  watch: {
    '$route.params.id'(){this.load()}
  },
  mounted(){
    this._applyThemeOnce();
    var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.load();
  },
  beforeUnmount(){if(this.themeObserver){this.themeObserver.disconnect();this.themeObserver=null}},
  methods:{
    _applyThemeOnce(){
      var current=document.documentElement.getAttribute('data-theme');
      var desired=(localStorage.getItem('flockr_theme')||current||'dark');
      if(current!==desired) document.documentElement.setAttribute('data-theme',desired);
      this.darkMode=desired!=='light';
    },
    goBack(){ if(window.history.length>1) this.$router.go(-1); else this.$router.push('/forum'); },
    relTime(ts){
      if(!ts) return '';
      var diff = Math.floor((Date.now() - new Date(ts.replace(' ','T')).getTime()) / 1000);
      if (diff < 60) return 'just now';
      if (diff < 3600) return Math.floor(diff/60) + 'm';
      if (diff < 86400) return Math.floor(diff/3600) + 'h';
      if (diff < 2592000) return Math.floor(diff/86400) + 'd';
      return ts.split(' ')[0];
    },
    canAccept(p){
      return this.isMyTopic && p.author_id !== this.myUserId && this.topic.is_answered != 1;
    },
    async load(){
      this.loading=true;
      var topicId=this.$route.params.id;
      try{
        var r=await executeJson({function:'getForumTopicDetail',topicId:topicId,userId:this.myUserId},flocker);
        if(r.data&&r.data.success&&r.data.data){
          this.topic=r.data.data.topic;
          this.posts=r.data.data.posts||[];
        }
      }catch(e){console.error(e)}
      this.loading=false;
    },
    mediaUrl(u){ if(!u) return ''; if(u.indexOf('http')===0) return u; return uploads + String(u).replace(/^uploads\//, ''); },
    postMedia(p){
      if (!p || !p.photos) return [];
      try {
        var arr = typeof p.photos === 'string' ? JSON.parse(p.photos) : p.photos;
        return Array.isArray(arr) ? arr.filter(function(m){return m && m.url;}) : [];
      } catch(e){ return []; }
    },
    postVisualMedia(p){ return this.postMedia(p).filter(function(m){ return m.type !== 'document'; }); },
    postDocs(p){ return this.postMedia(p).filter(function(m){ return m.type === 'document'; }); },

    // ---- reactions ----
    async reactTopic(){
      if(!this.myUserId){this.$router.push('/login?redirect=/forum/'+this.topic.id);return}
      try{var r=await executeJson({function:'reactToPost',userId:this.myUserId,targetType:'topic',targetId:this.topic.id,reaction:'like'},flocker);
        if(r.data&&r.data.success){this.topic.reaction_count=r.data.data.reactionCount;this.topic.my_reaction=r.data.data.myReaction;}}catch(e){}
    },
    async reactReply(p){
      if(!this.myUserId){this.$router.push('/login?redirect=/forum/'+this.topic.id);return}
      try{var r=await executeJson({function:'reactToPost',userId:this.myUserId,targetType:'post',targetId:p.id,reaction:'like'},flocker);
        if(r.data&&r.data.success){p.reaction_count=r.data.data.reactionCount;p.my_reaction=r.data.data.myReaction;}}catch(e){}
    },
    async toggleFollowTopic(){
      if(!this.myUserId){this.$router.push('/login?redirect=/forum/'+this.topic.id);return}
      var fn=this.topic.i_follow?'unfollowUser':'followUser';
      try{var r=await executeJson({function:fn,followerId:this.myUserId,followingId:this.topic.author_id},flocker);
        if(r.data&&r.data.success)this.topic.i_follow=this.topic.i_follow?null:1;}catch(e){}
    },

    // ---- share ----
    // Internal-share modal also exposes external-share buttons (FB, WA, X, Telegram, Copy, native).
    // External buttons don't require login — anyone who can read the post can share it onward
    // (growth-friendly: drives external clicks back into flockr.africa).
    openShare(){ this.shareModal={open:true,note:'',busy:false}; this.copiedFlash=false; },
    closeShare(){ this.shareModal={open:false,note:'',busy:false}; this.copiedFlash=false; },
    async shareNative(){
      if (!this.canWebShare) return;
      try {
        await navigator.share({ title: 'Flockr', text: this.shareText, url: this.shareUrl });
        this._recordExternalShare('native');
      } catch(e) { /* user cancelled */ }
    },
    shareTo(network){
      var url = encodeURIComponent(this.shareUrl);
      var text = encodeURIComponent(this.shareText);
      var dest = '';
      if (network === 'facebook') dest = 'https://www.facebook.com/sharer/sharer.php?u=' + url;
      else if (network === 'whatsapp') dest = 'https://wa.me/?text=' + text + '%20' + url;
      else if (network === 'twitter') dest = 'https://twitter.com/intent/tweet?text=' + text + '&url=' + url;
      else if (network === 'telegram') dest = 'https://t.me/share/url?url=' + url + '&text=' + text;
      else return;

      // Facebook stripped the `quote=` parameter from sharer.php years ago — no
      // developer can pre-fill the caption box now. Workaround: silently copy
      // the post body to the clipboard before opening Facebook so the user
      // only has to Cmd+V into "What's on your mind?".
      if (network === 'facebook' && this.topic && navigator.clipboard && navigator.clipboard.writeText) {
        var caption = (this.topic.content || this.topic.title || '').trim();
        if (caption) { navigator.clipboard.writeText(caption).catch(function(){}); }
      }

      window.open(dest, '_blank', 'noopener,noreferrer,width=600,height=600');
      this._recordExternalShare(network);
    },
    async copyShareLink(){
      var link = this.shareUrl;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(link);
        } else {
          var ta = document.createElement('textarea');
          ta.value = link; document.body.appendChild(ta); ta.select();
          document.execCommand('copy'); document.body.removeChild(ta);
        }
        this.copiedFlash = true;
        var self = this; setTimeout(function(){ self.copiedFlash = false; }, 1500);
        this._recordExternalShare('copy');
      } catch(e) { /* clipboard blocked — fail silently */ }
    },
    _recordExternalShare(network){
      // Best-effort analytics ping; ignored on failure.
      try {
        if (!this.topic || !this.topic.id) return;
        executeJson({function:'logForumExternalShare', topicId:this.topic.id, userId:this.myUserId, network:network}, flocker).catch(function(){});
      } catch(e){}
    },
    async confirmShare(){
      this.shareModal.busy=true;
      try{
        var orig=this.topic.shared_topic_id||this.topic.id;
        var r=await executeJson({function:'createFeedPost',authorId:this.myUserId,content:this.shareModal.note,sharedTopicId:orig},flocker);
        if(r.data&&r.data.success){this.topic.i_shared=1;this.topic.share_count=(Number(this.topic.share_count)||0)+1;this.closeShare();}
        else this.shareModal.busy=false;
      }catch(e){this.shareModal.busy=false;}
    },
    async unshareTopic(){
      if(!this.myUserId)return;
      var orig=this.topic.shared_topic_id||this.topic.id;
      try{var r=await executeJson({function:'unshareTopic',userId:this.myUserId,topicId:orig},flocker);
        if(r.data&&r.data.success){this.topic.i_shared=null;this.topic.share_count=Math.max(0,(Number(this.topic.share_count)||1)-1);}}catch(e){}
    },

    // ---- reply composer ----
    focusReply(){ var self=this; this.$nextTick(function(){ if(self.$refs.replyBox){ self.$refs.replyBox.focus(); self.$refs.replyBox.scrollIntoView({behavior:'smooth',block:'center'}); } }); },
    autoGrow(e){ var t=e.target; t.style.height='auto'; t.style.height=Math.min(t.scrollHeight,120)+'px'; },
    async onReplyMediaPick(event){
      var files = Array.from(event.target.files || []);
      var slots = 6 - this.replyMedia.length;
      if (slots <= 0) { event.target.value=''; return; }
      var picked = files.slice(0, slots);
      this.replyCompressing = true;
      try {
        for (var i = 0; i < picked.length; i++) {
          var f = picked[i];
          if (f.type.indexOf('image/') === 0) {
            var c = await compressImage(f, 1200, 0.8);
            this.replyMedia.push({ type:'image', file: c.file, preview: c.preview });
          } else if (f.type.indexOf('video/') === 0) {
            if (f.size > 25 * 1024 * 1024) { this.replyError = 'Videos must be under 25 MB.'; continue; }
            this.replyMedia.push({ type:'video', file: f, preview: URL.createObjectURL(f) });
          } else if (this.isDocFile(f)) {
            if (f.size > 10 * 1024 * 1024) { this.replyError = 'Documents must be under 10 MB.'; continue; }
            this.replyMedia.push({ type:'document', file: f, name: f.name, preview: '' });
          } else { this.replyError = 'Unsupported file: ' + (f.name || 'file'); }
        }
      } catch(e){ this.replyError = 'Could not process file'; }
      this.replyCompressing = false;
      event.target.value = '';
    },
    isDocFile(f){
      var allowedMime = ['application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document','application/vnd.ms-excel','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet','application/vnd.ms-powerpoint','application/vnd.openxmlformats-officedocument.presentationml.presentation','text/plain','text/csv'];
      var allowedExt = /\.(pdf|docx?|xlsx?|pptx?|txt|csv)$/i;
      return allowedMime.indexOf(f.type) > -1 || allowedExt.test(f.name || '');
    },
    docIcon(name){
      var n = (name || '').toLowerCase();
      if (n.indexOf('.pdf') > -1) return 'mdi-file-pdf-box';
      if (/\.docx?$/.test(n)) return 'mdi-file-word-box';
      if (/\.xlsx?$/.test(n) || n.indexOf('.csv') > -1) return 'mdi-file-excel-box';
      if (/\.pptx?$/.test(n)) return 'mdi-file-powerpoint-box';
      if (n.indexOf('.txt') > -1) return 'mdi-file-document-outline';
      return 'mdi-file-outline';
    },
    humanSize(b){
      b = Number(b||0); if (b < 1024) return b + ' B';
      if (b < 1024*1024) return (b/1024).toFixed(1) + ' KB';
      return (b/1024/1024).toFixed(1) + ' MB';
    },
    removeReplyMedia(idx){
      var m = this.replyMedia[idx];
      if (m && m.preview && m.preview.indexOf('blob:') === 0) URL.revokeObjectURL(m.preview);
      this.replyMedia.splice(idx, 1);
    },
    onReplyLocationToggle(){
      var self = this;
      if (!this.replyShareLocation) { this.replyLocationLabel=''; this.replyLocationLat=null; this.replyLocationLng=null; this.replyLocationError=''; return; }
      if (!navigator.geolocation) { this.replyShareLocation = false; this.replyLocationError = 'Geolocation not supported'; return; }
      this.replyLocationBusy = true; this.replyLocationError = '';
      navigator.geolocation.getCurrentPosition(async function(pos){
        self.replyLocationLat = pos.coords.latitude; self.replyLocationLng = pos.coords.longitude;
        try {
          var url = 'https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=' + self.replyLocationLat + '&lon=' + self.replyLocationLng + '&zoom=8';
          var resp = await fetch(url, { headers: { 'Accept-Language': 'en' } });
          var data = await resp.json();
          var a = data.address || {};
          var parts = [a.city || a.town || a.village || a.county || a.state_district || a.state, a.country];
          self.replyLocationLabel = parts.filter(Boolean).join(', ') || data.display_name || ('GPS ' + self.replyLocationLat.toFixed(2) + ', ' + self.replyLocationLng.toFixed(2));
        } catch(e){ self.replyLocationLabel = 'GPS ' + self.replyLocationLat.toFixed(2) + ', ' + self.replyLocationLng.toFixed(2); }
        self.replyLocationBusy = false;
      }, function(err){
        self.replyLocationBusy = false; self.replyShareLocation = false;
        self.replyLocationError = err && err.code === 1 ? 'Permission denied' : 'Could not detect location';
      }, { timeout: 8000, enableHighAccuracy: false });
    },
    async submitReply(){
      if(!this.myUserId){this.$router.push('/login?redirect=/forum/'+this.topic.id);return}
      this.replyBusy=true; this.replyError='';
      try{
        var fd = new FormData();
        fd.append('function','replyToForumTopic');
        fd.append('userId', this.myUserId);
        fd.append('topicId', this.topic.id);
        fd.append('content', this.replyContent);
        if (this.replyShareLocation && this.replyLocationLat) {
          fd.append('locationLabel', this.replyLocationLabel || '');
          fd.append('locationLat', String(this.replyLocationLat));
          fd.append('locationLng', String(this.replyLocationLng));
        }
        var pi = 0;
        this.replyMedia.forEach(function(m){ if (m.file) { fd.append('photo' + pi, m.file); pi++; } });
        var r = await execute(fd, flocker);
        if(r.data&&r.data.success){
          this.replyContent=''; this.replyMedia = [];
          this.replyShareLocation = false; this.replyLocationLabel = ''; this.replyLocationLat = null; this.replyLocationLng = null;
          if(this.$refs.replyBox) this.$refs.replyBox.style.height='auto';
          await this.load();
        }else{ this.replyError=(r.data&&r.data.message)||'Could not post reply'; }
      }catch(e){this.replyError=(e&&e.message)||'Network error'}
      this.replyBusy=false;
    },
    async acceptAnswer(p){
      if(!confirm("Mark this as the best answer? The post will be marked answered.")) return;
      this.acceptBusy=true;
      try{
        var r=await executeJson({function:'acceptForumAnswer',userId:this.myUserId,topicId:this.topic.id,postId:p.id},flocker);
        if(r.data&&r.data.success) await this.load();
      }catch(e){}
      this.acceptBusy=false;
    },
    async verifyPost(p){
      try{
        var r=await executeJson({function:'verifyForumPost',userId:this.myUserId,postId:p.id},flocker);
        if(r.data&&r.data.success) p.is_vet_verified = 1;
      }catch(e){}
    }
  }
};
</script>
